import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, Button, List, ListItem, Typography } from "@mui/material";
import {
  useCourseMappingNoteList,
  useCourseRenderingLoading,
  useCourseScoreCardDetails,
  useCourseScoreCardLoading,
} from "hooks/CourseMapDetails";
import asset from "json/assets";
import events from "json/events";
import Draggable from "react-draggable";
import eventEmitter from "services/event.emitter";
import styles from "styles/component/CustomCoursePopOver.module.scss";
import _style from "styles/component/CourseMappingNotePopOver.module.scss";
import * as _ from "underscore";
import CustomInput from "ui/CustomInput/CustomInput";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import {
  course_mapping_note_create,
  course_mapping_note_list,
} from "redux/CourseMapSlice";
import { useProfileData } from "hooks/ProfileDetails";
import { decryptSecretKey } from "services/utils";
import { useSearchParams } from "react-router-dom";

const schema = yup.object().shape({
  description: yup.string().trim().required(),
});

const NOTE_TABLE_MAX_HEIGHT = 140;

const CourseMappingNoteList = (props) => {
  const { title = "Mapping Notes" } = props;
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const profileData = useProfileData();
  const secret_Key = decryptSecretKey();
  const id_courseFromQuery = searchParams.get("course");

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { description: '' },
    resolver: yupResolver(schema),
  });

  const mappingNoteList = useCourseMappingNoteList()?.noteList;
  const scoreCardDetails = useCourseScoreCardDetails();
  const scoreCardStatus = useCourseScoreCardLoading();
  const renderingStatus = useCourseRenderingLoading();

  const [open, setOpen] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [notePos, setNotePos] = useState({ x: 0, y: 200 });
  const [tableMaxHeight, setTableMaxHeight] = useState(null);
  const noteContentRef = useRef(null);
  const hasUserDraggedRef = useRef(false);

  const infoSettled =
    scoreCardStatus !== "loading" && renderingStatus !== "loading";
  const infoExpected = Boolean(scoreCardDetails?.courseName);

  useEffect(() => {
    const handler = (e) => setOpen(e.visible);
    eventEmitter.on(events.openMappingNotesModal, handler);
    return () => eventEmitter.off(events.openMappingNotesModal, handler);
  }, []);

  useEffect(() => {
    const closeOnAttributePopover = (e) => {
      if (e?.visible || e?.open) {
        setOpen(false);
      }
    };
    eventEmitter.on(events.addMarkerToggle, closeOnAttributePopover);
    eventEmitter.on(events.openTreeElementInModal, closeOnAttributePopover);
    eventEmitter.on(events.openBunkerElementInModal, closeOnAttributePopover);
    return () => {
      eventEmitter.off(events.addMarkerToggle, closeOnAttributePopover);
      eventEmitter.off(events.openTreeElementInModal, closeOnAttributePopover);
      eventEmitter.off(events.openBunkerElementInModal, closeOnAttributePopover);
    };
  }, []);

  useEffect(() => {
    if (open) {
      const container = document.getElementById("note_table_container");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [mappingNoteList, open]);

  useLayoutEffect(() => {
    if (!open || !infoSettled || hasUserDraggedRef.current) return;
    let observedInfoEl = null;
    const observer = new ResizeObserver(() => reposition());
    const reposition = () => {
      if (hasUserDraggedRef.current) return;
      const infoEl = document.getElementById("course_info_popover");
      if (infoEl !== observedInfoEl) {
        if (observedInfoEl) observer.unobserve(observedInfoEl);
        if (infoEl) observer.observe(infoEl);
        observedInfoEl = infoEl;
      }
      const noteEl = noteContentRef.current;
      const offsetParent = noteEl?.offsetParent;
      const tableEl = document.getElementById("note_table_container");
      if (!offsetParent || !tableEl) return;
      const infoRect = infoEl?.getBoundingClientRect();
      if (!infoEl || !infoRect.height) {
        if (!infoExpected) {
          setPlaced(true);
        }
        return;
      }
      const parentRect = offsetParent.getBoundingClientRect();
      const gap = 15;
      const naturalTop = parentRect.top + noteEl.offsetTop;
      const naturalLeft = parentRect.left + noteEl.offsetLeft;
      const chromeHeight = noteEl.offsetHeight - tableEl.offsetHeight;
      const belowInfoY = Math.round(infoRect.bottom - naturalTop + gap);
      const spaceBelowInfo =
        window.innerHeight - naturalTop - belowInfoY - chromeHeight - gap;
      let nextX = 0;
      let nextY = belowInfoY;
      let nextTableMax = Math.min(
        NOTE_TABLE_MAX_HEIGHT,
        Math.floor(spaceBelowInfo)
      );
      if (nextTableMax < 96) {
        nextX = Math.round(infoRect.right - naturalLeft + gap);
        nextY = Math.max(0, Math.round(infoRect.top - naturalTop));
        nextTableMax = Math.max(
          96,
          Math.min(
            NOTE_TABLE_MAX_HEIGHT,
            Math.floor(
              window.innerHeight - naturalTop - nextY - chromeHeight - gap
            )
          )
        );
      }
      setTableMaxHeight(nextTableMax);
      setNotePos((prev) =>
        prev.x === nextX && prev.y === nextY ? prev : { x: nextX, y: nextY }
      );
      setPlaced(true);
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", reposition);
    };
  }, [open, infoSettled, infoExpected]);

  useEffect(() => {
    if (!open || placed) return;
    const fallbackTimer = setTimeout(() => setPlaced(true), 4000);
    return () => clearTimeout(fallbackTimer);
  }, [open, placed]);

  const handleDrag = (e, data) => {
    hasUserDraggedRef.current = true;
    setNotePos({ x: data.x, y: data.y });
  };


  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (mappingNoteList?.length) {
      setOpen(true);
    } 
  }, [mappingNoteList]);

  const onSubmit = useCallback((e) => {
    dispatch(
      course_mapping_note_create({
        body: {
          note: e.description,
          id_course: Number(id_courseFromQuery),
        },
        user: {
          secretKey: secret_Key,
          userName: profileData?.profile_information_data?.username,
        },
      })
    )?.then((res) => {
      dispatch(
        course_mapping_note_list({
          body: {
            id_course: id_courseFromQuery,
          },
          user: {
            secretKey: secret_Key,
            userName: profileData?.profile_information_data?.username,
          },
        })
      )?.then((res) => {
        // resetting text field after submission
        reset({
          description: "",
        });
      });
    });
  }, []);

  return (
    <Box className={` ${styles.draggableDiv}`}>
      <Draggable
        bounds={"#DraggableContainer"}
        position={notePos}
        onDrag={handleDrag}
        cancel={`.input,textarea,button,select,.new_font_size`}
      >
        <Box
          ref={noteContentRef}
          className={`${styles.poover_content} ${styles.default_poover_content} custom_popover_content ${_style.note_sec}`}
          sx={{
            opacity: open && placed ? "1" : "0",
            display: open ? "block" : "none",
            pointerEvents: open && placed ? "auto" : "none",
          }}
        >
          <Box className={styles.poover_head}>
            <Typography variant="h5">{title}</Typography>
            <List>
              <ListItem>
                <Button onClick={handleClose}>
                  <img src={asset.close} alt="" />
                </Button>
              </ListItem>
            </List>
          </Box>

          <Box className={`${styles.popover_ftr} ${_style.popover_scorecard}`}>
            <Box
              id="note_table_container"
              className={` ${_style.table_uppr}`}
              style={tableMaxHeight ? { maxHeight: tableMaxHeight } : undefined}
            >
              <table
                id={"list_table"}
                className={_style.score_table}
                style={{ userSelect: "none" }}
              >
                <thead>
                  {mappingNoteList?.length ? (
                    <>
                      {mappingNoteList?.map((note, index) => {
                        return (
                          <tr
                            key={index}
                            id={
                              index === mappingNoteList?.length - 1
                                ? "last_item"
                                : `item_${index}`
                            }
                          >
                            <td>
                              <Typography>
                                {note?.firstName}{" "}
                                {note?.lastName?.charAt(0)?.toUpperCase()}.
                              </Typography>
                              <Typography>
                                {moment(note?.dateCreated).format("MM/DD/YYYY")}
                              </Typography>
                            </td>
                            <td>
                              <Typography>{note?.note}</Typography>
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  ) : null}
 
                </thead>
              </table>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box className={`${_style.note_input}`}>
                <Controller
                  control={control}
                  name="description"
                  render={({ field: { onChange, value } }) => (
                    <CustomInput
                      placeholder="Add Note"
                      type="text"
                      value={value}
                      onChange={onChange}
                      disabled={false}
                      error={Boolean(errors?.description)}
                      helperText={errors?.description?.message}
                      className="new_font_size"
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" || e.key === "Delete") {
                          e.stopPropagation();
                        }
                      }}
                    />
                  )}
                />
                <Button type="submit" className={styles.blue_btn}>
                  Save
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Draggable>
    </Box>
  );
};

export default CourseMappingNoteList;
